import React, { useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { standardRequestWithErrorFlash } from 'shared/utils/request';
import { Person, SidePanelDetails, Row } from 'talent_matching/types';
import { SidePanelHandle } from 'shared/components/side_panel';
import { usePerson } from './use_people';

type UseCandidateSidePanelOptions = {
  applicationId: number;
  ref?: React.ForwardedRef<SidePanelHandle>;
  prefetchCandidatesBatch?: (Row | null | undefined)[];
};

type UseCandidateSidePanelReturn = {
  handlePanelRef: (node: SidePanelHandle | null) => void;
  panelRef: React.RefObject<SidePanelHandle>;
  application: Person;
  isLoading: boolean;
};

const personDetailsQuery = async (applicationId: number) => {
  const response = await standardRequestWithErrorFlash(
    `/talent_matching/candidates/${applicationId}`
  );

  const details: SidePanelDetails = response.data;

  return response.status === 200 ? details : null;
};

const useCandidateSidePanel = ({
  applicationId,
  ref,
  prefetchCandidatesBatch,
}: UseCandidateSidePanelOptions): UseCandidateSidePanelReturn => {
  const queryClient = useQueryClient();

  const { person, updatePerson } = usePerson(applicationId);

  // Main query to fetch candidate details for the side panel
  const { data: details = {}, isLoading } = useQuery({
    queryKey: ['person', 'details', applicationId],
    queryFn: () => personDetailsQuery(applicationId),
  });

  // Hydrate person with panel details
  useEffect(() => {
    updatePerson({ ...person, ...details });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  const panelRef = React.useRef<SidePanelHandle | null>(null);

  const handlePanelRef = useCallback(
    (node: SidePanelHandle | null) => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
      panelRef.current = node;
    },
    [ref]
  );

  // Prefetch other candidates in the batch for smoother navigation
  prefetchCandidatesBatch?.forEach((candidateToPrefetch) => {
    candidateToPrefetch?.id &&
      queryClient.prefetchQuery({
        queryKey: ['person', 'details', candidateToPrefetch.id],
        queryFn: () => personDetailsQuery(candidateToPrefetch.id),
      });
  });

  return {
    handlePanelRef,
    panelRef,
    application: person,
    isLoading,
  };
};

export default useCandidateSidePanel;
